import {gsap, Power4} from "gsap/gsap-core";

import {MotionPathPlugin} from "gsap/MotionPathPlugin";
import {PixiPlugin} from "gsap/PixiPlugin";
//
gsap.registerPlugin(PixiPlugin, MotionPathPlugin);


import {WeatherCard} from "./weather_card.js";
import {adaptColorsToWeather, adaptColorToDaytime} from "./weather_colors.js";
import {updateSunrise} from "../sunrise/sunrise.js";
import {currentTime, dateService} from "../datetime/time.js";
import {celsiusToFahrenheit, getTempUnits, initLocation} from "../settings/temp_units_switch.js";
import {mixpanel} from "../util/mixpanel";
import * as Sentry from "@sentry/browser";
import {fetchRetry} from "../util/network.js";
import {measureTime} from "../util/debug";
import {getLocationFromStorage} from "../util/geo_location";
import {updateVisitsAndShowModal} from "../settings/rating";
import i18next from "i18next";
import {updateLoginState, updateMinutePrecip, updatePrecip, updateTemp, updateWind} from "../settings/settings_common";
import {updateUVIndex} from "./uvi/uvi";
import {APP_VERSION} from "../util/version";
import {showErrorToast, showTooManyRequestsError} from "../util/errors";
import {createMinutePrecipChart} from "./temperature/temp_graphs";


async function setupDashboard() {
    let position = getLocationFromStorage();
    if (!position) {
        alert(i18next.t("errors.no_location"));
        return;
    }
    updateVisitsAndShowModal();
    console.log(position);
    await manager.updateWeather(position);
}


export class WeatherManager {
    constructor() {
        this.cards = Array.from(document.querySelectorAll('.container')).map(c => new WeatherCard(c));
        this.start = window.performance.now();
        this.brightness = 1;
        this.currentWeather = null;
        this.weatherJson = null;
        this.twilightSeconds = 30 * 60;
        this.minutePrecipChart = null;
    }

    async init() {
        console.log("init weather manager");
        await this.resize();
    }

    async resize() {
        for (const card of this.cards) {
            await card.resize();
        }
    }

    tick(timestamp) {
        if (!this.currentWeather) return;

        const elapsed = timestamp - this.start; // float in milliseconds
        if (this.brightness < 0.01 || this.brightness > 0.99) {
            if (elapsed > 60 * 1000) {
                this.start = timestamp;
                this.adaptToDaytime();
            }
        } else {
            // updates need to be more frequent during dusk/dawn
            if (elapsed > 20 * 1000) {
                this.start = timestamp;
                this.adaptToDaytime();
            }
        }
        this.cards.forEach(card => card.tick(timestamp));
    }

    #computeBrightness() {
        // get current time in seconds
        let now = dateService.getTimestampSeconds();
        let brightness;
        const sunriseTimestamp = this.currentWeather.sunrise;
        const sunsetTimestamp = this.currentWeather.sunset;
        if (now < sunriseTimestamp - this.twilightSeconds || now > sunsetTimestamp + this.twilightSeconds) {
            brightness = 0;
        } else if (now > sunriseTimestamp + this.twilightSeconds && now < sunsetTimestamp - this.twilightSeconds) {
            // day
            brightness = 1.0;
        } else {
            // compute percentage to make darker
            brightness = Math.min(
                Math.abs(now + this.twilightSeconds - sunriseTimestamp),
                Math.abs(now - this.twilightSeconds - sunsetTimestamp)) / (2 * this.twilightSeconds);
            // 0 -- dark , 1 - bright
            console.debug("Brightness Level:", brightness);
        }
        return brightness;
    }

    static #updateCurWeather(currentWeather) {
        if (getTempUnits() === "F") {
            document.getElementById("cur_temp").textContent = Math.round(celsiusToFahrenheit(currentWeather.temp));
        } else {
            document.getElementById("cur_temp").textContent = Math.round(currentWeather.temp);
        }
    }

    static #setLocality(weather_json) {
        let locality_text = document.getElementById("locality_text");
        locality_text.textContent = weather_json.location?.formatted_address;
        initLocation();
        gsap.fromTo(locality_text, {x: -50}, {duration: 1.5, opacity: 1, x: 0, ease: Power4.easeOut});
    }

    enableMinutelyWeather() {
        let chart_cont = document.querySelector('.precip-chart-minutely');
        // Remove existing canvas if it exists
        if (chart_cont.querySelector('canvas')) {
            chart_cont.removeChild(chart_cont.querySelector('canvas'));
        }
        chart_cont.classList.remove("hidden");
        let newCanvas = document.createElement('canvas');
        chart_cont.appendChild(newCanvas);
        this.minutePrecipChart = createMinutePrecipChart(
            newCanvas,
            0,
            10,
            this.weatherJson.minutely.map(x => x['precipitation']),
            Math.max(0.5, this.brightness)
        );

        // Start the recursive update loop with setTimeout
        setTimeout(() => {
            this.#updateMinutelyWeather();
        }, 60000); // 60000 ms = 1 minute
    }

    #updateMinutelyWeather() {
        // check minute updates were not disabled
        if (this.minutePrecipChart != null) {
            let dataset = this.minutePrecipChart.data.datasets[0];
            // Shift the data values array to the left and append the new value
            dataset.data.shift(); // Remove the first element
            // Update the chart to reflect the changes
            this.minutePrecipChart.update();
            setTimeout(() => {
                this.#updateMinutelyWeather();
            }, 60000); // 60000 ms = 1 minute
        }
    }

    disableMinutelyWeather() {
        let chart_cont = document.querySelector('.precip-chart-minutely');
        chart_cont.classList.add("hidden");
        if (chart_cont.querySelector('canvas')) {
            chart_cont.removeChild(chart_cont.querySelector('canvas'));
        }
        this.minutePrecipChart = null;
    }

    setWeather(weather_json) {
        this.weatherJson = weather_json;
        this.currentWeather = weather_json.current;

        this.brightness = this.#computeBrightness();
        WeatherManager.#setLocality(weather_json);
        WeatherManager.#updateCurWeather(this.currentWeather);

        this.cards.forEach((card, i) => {
            card.changeWeather(weather_json.daily[i]);
        });
        currentTime(weather_json.timezone);
        this.adaptToDaytime();
        this.setExtras(weather_json.extras);
    }

    async getWeatherJson(position) {
        const urlParams = new URLSearchParams(window.location.search);
        let requestParams = new URLSearchParams({lat: position.coords.latitude, lon: position.coords.longitude});
        let headers = {};
        if (urlParams.has("lang")) {
            requestParams.append("lang", urlParams.get("lang"));
        }
        if (urlParams.has("token")) {
            headers["ZIMA-AUTH-TOKEN"] = urlParams.get("token")
        }
        if (urlParams.has("provider")) {
            requestParams.append("provider", urlParams.get("provider"));
        }
        const weather_url = "https://zima-backend.farawaytech.com/v1/weather/forecast/daily?" + requestParams;
        let response = await fetchRetry(weather_url, {
            credentials: 'include',
            timeout: 5000,
            headers: headers
        }, 2);

        let weather_json;
        if (response === undefined) {
            if (urlParams.has("token")) {
                weather_json = await (await fetch("response-zurich.json")).json();
            }
            Sentry.captureMessage("Weather Request Error", "warning");
            showErrorToast();
        } else {
            let response_json = await response.json();
            if (response.status === 429) {
                if (response_json["email"]) {
                    showTooManyRequestsError();
                    updateLoginState(response_json["auth_data"]);
                } else {
                    const errorModal = document.getElementById('sign_in_modal');
                    errorModal.classList.remove("hidden");
                }
            } else {
                weather_json = response_json;
            }
        }
        return weather_json;
    }


    async updateWeather(position) {
        let weather_json = await this.getWeatherJson(position);
        if (!weather_json) {
            return;
        }
        sessionStorage.setItem("country_code", weather_json.location.country.short_name);
        sessionStorage.setItem("region_code", weather_json.location.region?.short_name);
        // IMPORTANT -- set weather first
        this.setWeather(weather_json);
        updateLoginState(weather_json["auth_data"]);
        // update features: minute precip, precip, temp, UVI, wind
        updateMinutePrecip();
        updatePrecip();
        updateTemp();
        updateUVIndex();
        updateWind();
        mixpanel.track('Weather', {
            "$latitude": weather_json["lat"],
            "$longitude": weather_json["lon"],
            "browser_lang": window.navigator.language,
            "result_lang": weather_json["lang"],
            "temp_units": getTempUnits(),
            "app_version": APP_VERSION
        });
    }

    setExtras(extras) {
        document.querySelectorAll("iframe").forEach(e => e.remove());
        extras.forEach(e => {
            if (e === "navigation/mapbox") {
                const iframe = document.createElement('iframe');
                iframe.setAttribute('src', `/extras/${e}`);
                iframe.id = e;
            } else {
                document.body.insertAdjacentHTML('afterbegin', `<iframe id="${e}" src="/extras/${e}"></iframe>`);
            }
        });
    }

    #computeColorConfig(weather_type) {
        const weatherColors = adaptColorsToWeather(weather_type);
        return adaptColorToDaytime(weatherColors, this.brightness);
    }

    #isSunrise() {
        let sunriseTimestamp = this.currentWeather.sunrise;
        let now = dateService.getTimestampSeconds();
        return now <= sunriseTimestamp + this.twilightSeconds;
    }

    /**
     * Return brightness level to properly set update frequency
     * @return number
     */
    adaptToDaytime() {
        this.brightness = this.#computeBrightness();
        updateSunrise(this.brightness, this.#isSunrise());
        this.cards.forEach(card => {
            card.brightness = this.brightness;
        });
        this.cards.forEach(card => card.adaptToDayTime(this.#computeColorConfig(card.currentWeather["type"])));
        // only get current weather from today's card
        document.getElementById("sky").className = this.cards[0].currentWeather["type"];
    }

}


export const manager = new WeatherManager();
const measuredSetupDashboard = measureTime(setupDashboard);

export {measuredSetupDashboard as setupDashboard};

